import { create } from "zustand";
import { api } from "../api/client";
import { ApiError, CurrentUser, Tenant } from "../api/types";

export type AuthStatus =
  | "idle"
  | "loading"
  | "authenticated"
  | "anonymous";

interface AuthState {
  status: AuthStatus;
  tenant: Tenant | null;
  currentUser: CurrentUser | null;

  // Server-state probe — called from AuthProvider on mount.
  loadTenant: () => Promise<void>;

  // Auth lifecycle (matches backend endpoints in internal/api/handlers/auth_*.go).
  signUp: (email: string, password: string) => Promise<void>;
  confirmEmail: (token: string) => Promise<string>;
  login: (email: string, password: string) => Promise<string>;
  logout: () => Promise<string>;
  forgotPassword: (email: string) => Promise<void>;
  resetPassword: (token: string, password: string) => Promise<void>;

  // Onboarding (post-login amoCRM OAuth connection).
  beginAmoCRMOAuth: () => Promise<string>;

  // Multi-account: activate another workspace / create a new one. Both
  // re-mint the session cookie server-side and return the redirect target;
  // callers should do a FULL navigation (window.location.href) so every
  // store re-hydrates against the new workspace.
  switchTenant: (tenantId: number) => Promise<string>;
  createWorkspace: (name: string) => Promise<string>;
  renameWorkspace: (name: string) => Promise<Tenant>;
}

export const useAuth = create<AuthState>((set) => ({
  status: "idle",
  tenant: null,
  currentUser: null,

  async loadTenant() {
    set({ status: "loading" });
    try {
      // /api/me is the session probe: id (for "My/Shared" tabs), role
      // (owner-only UI), and the workspace list (account switcher).
      const currentUser = await api.get<CurrentUser>("/api/me");

      // The tenant fetch is separate because it can legitimately 404 for an
      // authenticated user with zero workspaces (removed from their last
      // one) — AuthProvider routes that state to /create-workspace.
      let tenant: Tenant | null = null;
      if (currentUser.tenant_id !== 0) {
        try {
          tenant = await api.get<Tenant>("/api/tenant");
        } catch (err) {
          if (!(err instanceof ApiError && err.status === 404)) throw err;
        }
      }
      set({ status: "authenticated", tenant, currentUser });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        set({ status: "anonymous", tenant: null, currentUser: null });
        return;
      }
      // Network or 5xx — still treat as anonymous so the SPA shows /login;
      // the user can retry from there.
      set({ status: "anonymous", tenant: null, currentUser: null });
    }
  },

  async signUp(email, password) {
    // Server always returns 200 {ok:true} unless validation fails or
    // rate-limit triggers — duplicate emails are silently swallowed
    // (anti-enumeration). Caller renders "check your inbox" regardless.
    await api.post<{ ok: boolean }>("/auth/signup", { email, password });
  },

  async confirmEmail(token) {
    // Backend handler is GET /auth/confirm-email?token=... (no body).
    const res = await api.get<{ redirect: string }>(
      `/auth/confirm-email?token=${encodeURIComponent(token)}`,
    );
    return res.redirect ?? "/login?confirmed=1";
  },

  async login(email, password) {
    const res = await api.post<{ redirect: string }>("/auth/login", {
      email,
      password,
    });
    return res.redirect ?? "/chat";
  },

  async logout() {
    let target = "/login";
    try {
      const res = await api.post<{ redirect: string }>("/auth/logout");
      target = res.redirect ?? "/login";
    } catch {
      // Even if the backend logout call fails, the client clears its state.
    }
    set({ status: "anonymous", tenant: null, currentUser: null });
    return target;
  },

  async forgotPassword(email) {
    // Server always returns 200 (anti-enum). Caller renders "if that
    // email is registered, you'll receive a link."
    await api.post<{ ok: boolean }>("/auth/forgot-password", { email });
  },

  async resetPassword(token, password) {
    await api.post<{ ok: boolean }>("/auth/reset-password", {
      token,
      password,
    });
  },

  async beginAmoCRMOAuth() {
    const res = await api.post<{ authorization_url: string }>(
      "/api/onboarding/amocrm/oauth",
    );
    return res.authorization_url;
  },

  async switchTenant(tenantId) {
    const res = await api.post<{ redirect: string }>("/api/tenants/switch", {
      tenant_id: tenantId,
    });
    return res.redirect ?? "/chat";
  },

  async createWorkspace(name) {
    const res = await api.post<{ redirect: string }>("/api/tenants", { name });
    return res.redirect ?? "/onboarding";
  },

  async renameWorkspace(name) {
    const tenant = await api.put<Tenant>("/api/tenant/rename", { name });
    set((state) => ({
      tenant: state.tenant ? { ...state.tenant, name: tenant.name } : tenant,
      currentUser: state.currentUser
        ? {
            ...state.currentUser,
            tenants: state.currentUser.tenants.map((ws) =>
              ws.id === tenant.id ? { ...ws, name: tenant.name } : ws,
            ),
          }
        : null,
    }));
    return tenant;
  },
}));
